import { useEffect, useState } from "react";
import { AlertTriangle, ClipboardCheck, Mail, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { PENDING_CONFIRMATIONS_PANEL } from "@/constants/testIds";
import { toast } from "sonner";

const KIND_META = {
  acuse: { className: "bg-petroleum/10 text-petroleum" },
  firma: { className: "bg-amber-50 text-amber-700" },
};

export function PendingConfirmationsPanel() {
  const [rows, setRows] = useState(null);
  const [sending, setSending] = useState(false);

  const load = () => api.get("/dashboard/pending-confirmations-overview").then(({ data }) => setRows(data));

  useEffect(() => { load(); }, []);

  const sendReminders = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/dashboard/send-pending-signature-reminders");
      data.sent ? toast.success(data.message) : toast.warning(data.message);
    } catch {
      toast.error("No se pudo enviar el recordatorio.");
    } finally {
      setSending(false);
    }
  };

  if (!rows) return null;

  return (
    <div data-testid={PENDING_CONFIRMATIONS_PANEL.section} className="bg-card border border-petroleum/10 rounded-2xl p-5 mt-6">
      <div className="flex items-center justify-between gap-3 mb-3">
        <p className="text-sm font-semibold text-charcoal flex items-center gap-1.5">
          <ClipboardCheck className="w-4 h-4 text-terracotta" strokeWidth={1.5} /> Pendientes de firma o confirmación (todas las personas)
        </p>
        {rows.length > 0 && (
          <Button data-testid={PENDING_CONFIRMATIONS_PANEL.sendReminderButton} size="sm" variant="outline" disabled={sending} onClick={sendReminders} className="rounded-full h-7 text-xs gap-1.5 shrink-0">
            {sending ? <Loader2 className="w-3 h-3 animate-spin" /> : <Mail className="w-3 h-3" />} Enviar recordatorio a los pendientes
          </Button>
        )}
      </div>

      <div className="space-y-1.5 max-h-80 overflow-y-auto">
        {rows.map((r, i) => (
          <div key={i} data-testid={PENDING_CONFIRMATIONS_PANEL.row} className="flex items-center justify-between gap-2 text-sm py-1.5 border-b border-petroleum/5 last:border-0">
            <div className="min-w-0">
              <p className="text-charcoal truncate">{r.employee_name} <span className="text-charcoal/40">·</span> {r.title}</p>
              <p className="text-xs text-charcoal/50">
                {r.status_label}
                <span data-testid={PENDING_CONFIRMATIONS_PANEL.lastReminderLabel} className="text-charcoal/40">
                  {" · "}
                  {r.last_reminder_days_ago === null
                    ? "sin recordatorio enviado"
                    : r.last_reminder_days_ago === 0
                      ? "recordatorio enviado hoy"
                      : `último recordatorio hace ${r.last_reminder_days_ago} día(s)`}
                </span>
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {r.needs_reminder && (
                <span data-testid={PENDING_CONFIRMATIONS_PANEL.reminderBadge} className="text-xs font-medium text-amber-700 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" /> {r.days_pending}d
                </span>
              )}
              <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${KIND_META[r.kind]?.className}`}>
                {r.kind_label}
              </span>
            </div>
          </div>
        ))}
        {rows.length === 0 && (
          <p data-testid={PENDING_CONFIRMATIONS_PANEL.emptyState} className="text-sm text-charcoal/50">
            Nadie tiene documentos ni políticas pendientes de firma o confirmación.
          </p>
        )}
      </div>
    </div>
  );
}
